import { useEffect, useState, useRef } from 'react';
import {
  Box,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ClickAwayListener,
  Typography,
  CircularProgress,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { t } from 'i18next';

import { position } from '@/assets';
import { MobileInput } from './MobileInput';
import { useGoogleMapsAPI } from '@/hooks/useGoogleMapsAPI';
import { useToast } from '@/components/feedback';

interface Props {
  onChangeCoordinates: ({ lat, lng }: { lat: number; lng: number }) => void;
  getCoordinates: () => void;
  mapKey: string;
  disabled?: boolean;
  isGettingLocation?: boolean;
}

export const Autocomplete = ({
  onChangeCoordinates,
  getCoordinates,
  mapKey,
  disabled = false,
  isGettingLocation = false,
}: Props) => {
  const [positionName, setPositionName] = useState<string>('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null
  );

  const { isLoaded, error } = useGoogleMapsAPI(mapKey);
  const { showError } = useToast();

  useEffect(() => {
    if (isLoaded) {
      initializeAutocomplete();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (error) {
      console.error('Failed to load Google Maps API:', error);
    }
  }, [error]);

  // Initialize autocomplete services
  const initializeAutocomplete = () => {
    if (!window.google?.maps?.places) return;

    // Initialize autocomplete service for predictions
    autocompleteServiceRef.current =
      new window.google.maps.places.AutocompleteService();

    // Initialize places service for place details
    const mapDiv = document.createElement('div');
    const map = new window.google.maps.Map(mapDiv);
    placesServiceRef.current = new window.google.maps.places.PlacesService(map);
  };

  // Get place predictions
  const getPlacePredictions = (input: string) => {
    if (!autocompleteServiceRef.current || !input.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: input,
        types: ['geocode'],
      },
      (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setPredictions(predictions);
          setShowDropdown(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      }
    );
  };

  // Get place details by place_id
  const getPlaceDetails = (placeId: string, description: string) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId: placeId,
        fields: ['geometry', 'formatted_address', 'name'],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          if (place.geometry?.location) {
            const newLat = place.geometry.location.lat();
            const newLng = place.geometry.location.lng();
            if (newLat != null && newLng != null) {
              setLat(newLat);
              setLng(newLng);
              setPositionName(description);
              setShowDropdown(false);
            }
          }
        }
      }
    );
  };

  useEffect(() => {
    return () => {
      // Cleanup is handled automatically by the services
    };
  }, []);

  const onChange = (value: string) => {
    setPositionName(value);

    // Don't make API calls if disabled
    if (disabled) {
      showError(t('please_unlock_to_change') || 'Please unlock to change');
      return;
    }

    // Debounce the API calls to avoid excessive requests
    const timeoutId = setTimeout(() => {
      getPlacePredictions(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handlePredictionClick = (
    prediction: google.maps.places.AutocompletePrediction
  ) => {
    getPlaceDetails(prediction.place_id, prediction.description);
  };

  const handleClickAway = () => {
    setShowDropdown(false);
  };

  const handleLocationButtonClick = () => {
    if (disabled) {
      showError(t('please_unlock_to_write') || 'Please unlock to write');
      return;
    }
    if (isGettingLocation) {
      return;
    }
    getCoordinates();
  };

  const handleInputClick = () => {
    if (disabled) {
      showError(t('please_unlock_to_change') || 'Please unlock to change');
    }
  };
  useEffect(() => {
    // Use explicit null checks to handle valid zero coordinates (equator/prime meridian)
    if (lat !== null && lng !== null) {
      onChangeCoordinates({ lat, lng });
    }
  }, [lat, lng]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <MobileInput
          label={`${t('position_edit_site')}`}
          name='position'
          error={false}
          helperText={''}
          onChange={value => onChange(value.target.value)}
          onClick={handleInputClick}
          value={positionName}
          inputRef={inputRef as React.RefObject<HTMLInputElement>}
          search
          placeholder={''}
          disabled={disabled}
          endAdornment={
            <InputAdornment
              sx={{
                cursor:
                  disabled || isGettingLocation ? 'not-allowed' : 'pointer',
              }}
              position='end'
              onClick={handleLocationButtonClick}
            >
              {isGettingLocation ? (
                <CircularProgress
                  size={20}
                  sx={{
                    color: '#1C5B9C',
                  }}
                />
              ) : (
                <Box
                  component='img'
                  src={position}
                  sx={{
                    opacity: disabled ? 0.5 : 1,
                    filter: disabled ? 'grayscale(100%)' : 'none',
                  }}
                />
              )}
            </InputAdornment>
          }
        />
        {!disabled && showDropdown && predictions.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              borderRadius: '0.75rem',
              mt: 0.5,
              maxHeight: '300px',
              overflowY: 'auto',
              backgroundColor: '#fff',
            }}
          >
            <List sx={{ py: 0 }}>
              {predictions.map((prediction, index) => {
                // Split the description to separate Hebrew and English parts
                const parts = prediction.description.split(',');
                const hebrewPart = parts[0]?.trim() || '';
                const englishPart = parts.slice(1).join(',').trim() || '';

                return (
                  <ListItem
                    key={prediction.place_id}
                    onClick={() => handlePredictionClick(prediction)}
                    sx={{
                      cursor: 'pointer',
                      py: 1.5,
                      px: 2,
                      borderBottom:
                        index < predictions.length - 1
                          ? '1px solid #f0f0f0'
                          : 'none',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: '#6c757d' }}>
                      <LocationOn fontSize='small' />
                    </ListItemIcon>
                    <ListItemText
                      sx={{ margin: 0 }}
                      primary={
                        <Typography
                          component='span'
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.95rem',
                            color: '#212529',
                            display: 'block',
                          }}
                        >
                          {hebrewPart}
                        </Typography>
                      }
                      secondary={
                        englishPart ? (
                          <Typography
                            component='span'
                            sx={{
                              fontSize: '0.85rem',
                              color: '#6c757d',
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            {englishPart}
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
